// src/components/Sections/Admin/CodigosManuales/CodigosManualesTable.jsx
import React, { useState, useEffect } from 'react';
import {
  Table, Input, Select, Button, Tag, Space, Card,
  Tooltip, Typography, App, Badge
} from 'antd';
import {
  SearchOutlined, PlusOutlined, EditOutlined, DeleteOutlined,
  CheckCircleOutlined, CloseCircleOutlined, PercentageOutlined,
  DollarOutlined, TagOutlined, AppstoreOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import CodigosManuales from '../../../../service/CodigosManuales';
import { useNotify, useWithNotify } from '../../../../providers/NotificationProvider';

const { Title, Text } = Typography;

const CodigosManualesTable = () => {
  const navigate = useNavigate();
  const notify = useNotify();
  const withNotify = useWithNotify();
  const { modal } = App.useApp();

  const [codigos, setCodigos]     = useState([]);
  const [loading, setLoading]     = useState(false);
  const [searchText, setSearchText] = useState('');
  const [estadoFilter, setEstadoFilter] = useState('all');
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });

  const loadCodigos = async (page = 1, limit = 10) => {
    try {
      setLoading(true);
      const params = {
        page, limit,
        ...(estadoFilter !== 'all' && { activo: estadoFilter === 'activo' }),
        ...(searchText && { search: searchText }),
      };
      const res = await CodigosManuales.List(params);
      setCodigos(res.data || []);
      setPagination({
        current: res.pagination?.page || 1,
        pageSize: res.pagination?.limit || 10,
        total: res.pagination?.total || 0,
      });
    } catch (error) {
      notify.error(error.message || 'Error al cargar códigos manuales');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCodigos(pagination.current, pagination.pageSize);
  }, [estadoFilter]);

  const handleSearch = () => loadCodigos(1, pagination.pageSize);

  const handleToggle = async (id) => {
    try {
      await CodigosManuales.ToggleActivo(id);
      loadCodigos(pagination.current, pagination.pageSize);
    } catch (error) {
      notify.error('Error al cambiar estado');
    }
  };

  const handleDelete = (record) => {
    modal.confirm({
      title: `¿Eliminar código "${record.codigo}"?`,
      content: 'Esta acción no se puede deshacer.',
      okText: 'Eliminar',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        await withNotify(
          CodigosManuales.Delete(record._id),
          { loading: 'Eliminando...', success: 'Código eliminado', error: 'Error al eliminar' }
        );
        loadCodigos(pagination.current, pagination.pageSize);
      },
    });
  };

  const columns = [
    {
      title: 'Código',
      dataIndex: 'codigo',
      key: 'codigo',
      render: (v) => (
        <span className="font-mono font-bold text-blue-700 bg-blue-50 px-2 py-0.5 rounded text-sm">
          {v}
        </span>
      ),
    },
    {
      title: 'Nombre',
      dataIndex: 'nombre',
      key: 'nombre',
      render: (v, r) => (
        <div>
          <div className="font-medium text-slate-800 text-sm">{v}</div>
          {r.descripcion && (
            <div className="text-xs text-slate-400 truncate max-w-xs">{r.descripcion}</div>
          )}
        </div>
      ),
    },
    {
      title: 'Descuento',
      key: 'descuento',
      render: (_, r) => (
        <Tag
          icon={r.tipoDescuento === 'porcentaje' ? <PercentageOutlined /> : <DollarOutlined />}
          color={r.tipoDescuento === 'porcentaje' ? 'blue' : 'green'}
        >
          {r.tipoDescuento === 'porcentaje'
            ? `${r.valorDescuento}%`
            : `$${new Intl.NumberFormat('es-CL').format(r.valorDescuento)}`}
        </Tag>
      ),
    },
    {
      title: 'Talleres',
      key: 'talleres',
      render: (_, r) => {
        const count = r.talleresAplicables?.length || 0;
        return count === 0
          ? <Tag icon={<AppstoreOutlined />} color="purple">Todos</Tag>
          : <Tag icon={<AppstoreOutlined />}>{count} taller{count > 1 ? 'es' : ''}</Tag>;
      },
    },
    {
      title: 'Usos',
      key: 'usos',
      render: (_, r) => (
        <div className="text-sm">
          <span className="font-semibold text-slate-700">{r.usoActual || 0}</span>
          {r.usoMaximo ? (
            <span className="text-slate-400"> / {r.usoMaximo}</span>
          ) : (
            <span className="text-slate-400"> / ∞</span>
          )}
        </div>
      ),
    },
    {
      title: 'Estado',
      key: 'activo',
      render: (_, r) => (
        <Tag
          icon={r.activo ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
          color={r.activo ? 'success' : 'error'}
          className="cursor-pointer"
          onClick={() => handleToggle(r._id)}
        >
          {r.activo ? 'Activo' : 'Inactivo'}
        </Tag>
      ),
    },
    {
      title: 'Acciones',
      key: 'acciones',
      render: (_, r) => (
        <Space size="small">
          <Tooltip title="Editar">
            <Button
              size="small" type="text" icon={<EditOutlined />}
              onClick={() => navigate(`/admin/codigos-manuales/editar/${r._id}`)}
            />
          </Tooltip>
          <Tooltip title="Eliminar">
            <Button
              size="small" type="text" danger icon={<DeleteOutlined />}
              onClick={() => handleDelete(r)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div className="p-4 md:p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <Title level={4} className="!mb-0 flex items-center gap-2">
            <TagOutlined className="text-amber-500" />
            Códigos de Descuento Manuales
          </Title>
          <Text type="secondary" className="text-sm">
            Códigos que el vendedor ingresa manualmente en ventas presenciales
          </Text>
        </div>
        <Button
          type="primary" icon={<PlusOutlined />}
          onClick={() => navigate('/admin/codigos-manuales/crear')}
          style={{ background: '#feda2c', borderColor: '#feda2c', color: '#0d1117' }}
        >
          Nuevo Código
        </Button>
      </div>

      {/* Filtros */}
      <Card size="small">
        <div className="flex gap-3 flex-wrap">
          <Input
            placeholder="Buscar por código o nombre..."
            prefix={<SearchOutlined className="text-slate-400" />}
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            onPressEnter={handleSearch}
            className="max-w-xs"
            allowClear
          />
          <Select
            value={estadoFilter}
            onChange={setEstadoFilter}
            style={{ width: 140 }}
            options={[
              { label: 'Todos', value: 'all' },
              { label: 'Activos', value: 'activo' },
              { label: 'Inactivos', value: 'inactivo' },
            ]}
          />
          <Button onClick={handleSearch} icon={<SearchOutlined />}>Buscar</Button>
        </div>
      </Card>

      {/* Tabla */}
      <Card>
        <Table
          dataSource={codigos}
          columns={columns}
          rowKey="_id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showTotal: (total) => `${total} códigos`,
            onChange: (page, pageSize) => loadCodigos(page, pageSize),
          }}
          locale={{ emptyText: 'No hay códigos manuales. Crea el primero.' }}
        />
      </Card>
    </div>
  );
};

export default CodigosManualesTable;
